import { defineStore } from 'pinia';
import type { UserRole, DeviceType, Table, Device, AccessUser, LoginJournalEntry } from '../shared/types';

const DESKTOP_SESSION_STORAGE_KEY = 'cafe-desktop-session';

interface StoredDesktopSession {
  user: AccessUser | null;
  loginEntry: LoginJournalEntry | null;
}

function readStoredDesktopSession(): StoredDesktopSession {
  if (typeof window === 'undefined') {
    return {
      user: null,
      loginEntry: null,
    };
  }

  try {
    const stored = window.localStorage.getItem(DESKTOP_SESSION_STORAGE_KEY);
    if (!stored) {
      return {
        user: null,
        loginEntry: null,
      };
    }

    const parsed = JSON.parse(stored) as StoredDesktopSession | null;
    const parsedUser = parsed?.user && parsed.user.id ? parsed.user : null;
    const parsedLoginEntry = parsed?.loginEntry && parsed.loginEntry.id ? parsed.loginEntry : null;

    if (parsedUser && parsedLoginEntry && parsedLoginEntry.userId !== parsedUser.id) {
      window.localStorage.removeItem(DESKTOP_SESSION_STORAGE_KEY);
      return {
        user: null,
        loginEntry: null,
      };
    }

    return {
      user: parsedUser,
      loginEntry: parsedLoginEntry,
    };
  } catch (error) {
    console.warn('Failed to read desktop session', error);
    return {
      user: null,
      loginEntry: null,
    };
  }
}

const initialDesktopSession = readStoredDesktopSession();

export const useSessionStore = defineStore('session', {
  state: () => ({
    // current mode/role/device context placeholder
    role: 'table' as UserRole,
    deviceId: '',
    deviceType: 'table' as DeviceType,
    tableId: '',
    currentTable: null as Table | null,
    currentDevice: null as Device | null,
    desktopUser: initialDesktopSession.user as AccessUser | null,
    desktopLoginEntry: initialDesktopSession.loginEntry as LoginJournalEntry | null,
  }),
  actions: {
    setCurrentTable(table: Table | null) {
      this.currentTable = table;
      this.tableId = table?.id || '';
    },
    clearCurrentTable() {
      this.currentTable = null;
      this.tableId = '';
    },
    setDesktopUser(user: AccessUser | null, loginEntry: LoginJournalEntry | null = null) {
      this.desktopUser = user;
      this.desktopLoginEntry = loginEntry;

      if (typeof window === 'undefined') {
        return;
      }

      if (!user && !loginEntry) {
        window.localStorage.removeItem(DESKTOP_SESSION_STORAGE_KEY);
        return;
      }

      window.localStorage.setItem(DESKTOP_SESSION_STORAGE_KEY, JSON.stringify({
        user,
        loginEntry,
      }));
    },
    clearDesktopUser() {
      this.setDesktopUser(null, null);
    },
  },
});
